import { useEffect, useLayoutEffect, useMemo, useRef, useState, type CSSProperties } from 'react';
import type { DashboardData, TopicRecord } from '../../types';
import { formatNumber } from '../../utils/dataTransforms';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { Flip } from 'gsap/Flip';
import { interpolate as flubberInterpolate } from 'flubber';
import { sankey, sankeyLinkHorizontal } from 'd3-sankey';
import { hierarchy, treemap as d3Treemap, type HierarchyRectangularNode } from 'd3-hierarchy';

const CYPRUS_LOCATIONS = ['Nicosia', 'Limassol', 'Larnaca', 'Paphos'] as const;
type CyprusLocation = (typeof CYPRUS_LOCATIONS)[number];

type Layer2AnswerKey = 'currentGov' | 'previousAdmins' | 'globalFactors' | 'staffing';

type SankeyNodeGroup = 'region' | 'target';

interface InfographicPageProps {
  data: DashboardData;
  week: string;
  onBack: () => void;
}

interface SourceTile {
  outlet: string;
  readers: number;
  sentiment: number;
}

interface LocationStory {
  location: CyprusLocation;
  totalReaders: number;
  healthTemperature: number;
  sentiment: number;
  blameCurrentGov: number;
  blameOthers: number;
  proWeight: number;
  antiWeight: number;
  sources: SourceTile[];
  topics: string[];
}

interface RegionSchema {
  id: CyprusLocation;
  name: string;
  avgSentiment: number;
  politicalWeight: number;
  healthTemperature: number;
  sample: number;
  proWeight: number;
  antiWeight: number;
  sources: SourceTile[];
  topics: string[];
}

interface SankeyNodeSchema {
  id: string;
  name: string;
  group: SankeyNodeGroup;
}

interface SankeyLinkSchema {
  source: string;
  target: string;
  value: number;
}

interface SankeyNodeLayout extends SankeyNodeSchema {
  x0: number;
  x1: number;
  y0: number;
  y1: number;
}

interface SankeyLinkLayout extends SankeyLinkSchema {
  id: string;
  path: string;
  width: number;
}

interface SankeyGraphNode extends SankeyNodeSchema {
  x0?: number;
  x1?: number;
  y0?: number;
  y1?: number;
}

interface SankeyGraphLink extends SankeyLinkSchema {
  value: number;
}

interface TreemapSchema {
  name: string;
  value?: number;
  sentiment?: number;
  children?: TreemapSchema[];
}

interface ScrollyPayload {
  regions: RegionSchema[];
  sankeyData: { nodes: SankeyNodeSchema[]; links: SankeyLinkSchema[] };
  treemapData: TreemapSchema;
}

interface SectionTreemapLayout {
  sources: HierarchyRectangularNode<TreemapSchema>[];
  answers: HierarchyRectangularNode<TreemapSchema>[];
}

interface TakeawayRow {
  signal: string;
  whatChanged: string;
  possibleDriver: string;
  whyItMatters: string;
}

interface PollCampaignLayer {
  id: string;
  title: string;
  question: string;
  options: string[];
}

const INFOGRAPHIC_WEEKS = [
  '2026-01-05',
  '2026-01-12',
  '2026-01-19',
  '2026-01-26',
  '2026-02-02',
  '2026-02-09',
  '2026-02-16',
  '2026-02-23'
] as const;
type InfographicWeek = (typeof INFOGRAPHIC_WEEKS)[number];

interface WeeklyRegionProfile {
  sampleScale: number;
  sentimentShift: number;
  antiShift: number;
  sourceReaderScale: [number, number, number, number];
}

interface WeeklyProfile {
  saturation: number;
  topicScale: number;
  topicPolarization: number;
  answerBias: Record<Layer2AnswerKey, number>;
  regions: Record<CyprusLocation, WeeklyRegionProfile>;
}

const WEEKLY_PROFILES: Record<InfographicWeek, WeeklyProfile> = {
  '2026-01-05': {
    saturation: 0.76,
    topicScale: 0.84,
    topicPolarization: 0.44,
    answerBias: { currentGov: 22, previousAdmins: -10, globalFactors: -8, staffing: -4 },
    regions: {
      Nicosia: { sampleScale: 1.26, sentimentShift: -0.26, antiShift: 0.2, sourceReaderScale: [1.54, 0.78, 0.62, 0.58] },
      Limassol: { sampleScale: 0.96, sentimentShift: -0.18, antiShift: 0.12, sourceReaderScale: [1.2, 0.92, 0.74, 0.66] },
      Larnaca: { sampleScale: 0.74, sentimentShift: -0.16, antiShift: 0.1, sourceReaderScale: [1.3, 0.8, 0.66, 0.6] },
      Paphos: { sampleScale: 0.62, sentimentShift: -0.08, antiShift: 0.06, sourceReaderScale: [1.12, 0.74, 0.76, 0.92] }
    }
  },
  '2026-01-12': {
    saturation: 0.88,
    topicScale: 0.92,
    topicPolarization: 0.3,
    answerBias: { currentGov: 14, previousAdmins: 6, globalFactors: -12, staffing: -8 },
    regions: {
      Nicosia: { sampleScale: 1.04, sentimentShift: -0.12, antiShift: 0.1, sourceReaderScale: [1.22, 1, 0.78, 0.72] },
      Limassol: { sampleScale: 1.2, sentimentShift: -0.14, antiShift: 0.12, sourceReaderScale: [1.08, 1.34, 0.76, 0.68] },
      Larnaca: { sampleScale: 0.82, sentimentShift: -0.1, antiShift: 0.08, sourceReaderScale: [1.12, 1.08, 0.72, 0.66] },
      Paphos: { sampleScale: 0.74, sentimentShift: -0.04, antiShift: 0.03, sourceReaderScale: [1, 0.96, 0.82, 0.9] }
    }
  },
  '2026-01-19': {
    saturation: 0.98,
    topicScale: 1.02,
    topicPolarization: 0.12,
    answerBias: { currentGov: -8, previousAdmins: 18, globalFactors: -8, staffing: -2 },
    regions: {
      Nicosia: { sampleScale: 0.9, sentimentShift: -0.04, antiShift: 0.02, sourceReaderScale: [0.98, 1.08, 0.92, 0.9] },
      Limassol: { sampleScale: 1.08, sentimentShift: -0.02, antiShift: 0.03, sourceReaderScale: [0.92, 1.14, 0.94, 0.92] },
      Larnaca: { sampleScale: 1.24, sentimentShift: 0.01, antiShift: -0.01, sourceReaderScale: [0.94, 1.2, 0.9, 0.88] },
      Paphos: { sampleScale: 0.72, sentimentShift: 0.05, antiShift: -0.04, sourceReaderScale: [0.88, 1.06, 1, 1.02] }
    }
  },
  '2026-01-26': {
    saturation: 1.08,
    topicScale: 1.12,
    topicPolarization: -0.08,
    answerBias: { currentGov: -12, previousAdmins: 4, globalFactors: 10, staffing: -2 },
    regions: {
      Nicosia: { sampleScale: 0.94, sentimentShift: 0.03, antiShift: -0.02, sourceReaderScale: [0.92, 0.94, 1.18, 1.02] },
      Limassol: { sampleScale: 1.1, sentimentShift: 0.05, antiShift: -0.04, sourceReaderScale: [0.88, 0.96, 1.22, 1.04] },
      Larnaca: { sampleScale: 1.14, sentimentShift: 0.07, antiShift: -0.05, sourceReaderScale: [0.9, 0.98, 1.28, 1.08] },
      Paphos: { sampleScale: 0.8, sentimentShift: 0.1, antiShift: -0.07, sourceReaderScale: [0.86, 0.92, 1.14, 1.18] }
    }
  },
  '2026-02-02': {
    saturation: 1.14,
    topicScale: 1.2,
    topicPolarization: -0.16,
    answerBias: { currentGov: -8, previousAdmins: -10, globalFactors: 6, staffing: 16 },
    regions: {
      Nicosia: { sampleScale: 0.86, sentimentShift: 0.05, antiShift: -0.04, sourceReaderScale: [0.88, 0.92, 1.02, 1.24] },
      Limassol: { sampleScale: 0.98, sentimentShift: 0.08, antiShift: -0.07, sourceReaderScale: [0.84, 0.96, 1.04, 1.28] },
      Larnaca: { sampleScale: 1.18, sentimentShift: 0.1, antiShift: -0.08, sourceReaderScale: [0.82, 0.9, 1.08, 1.34] },
      Paphos: { sampleScale: 1.12, sentimentShift: 0.13, antiShift: -0.1, sourceReaderScale: [0.8, 0.88, 1.02, 1.42] }
    }
  },
  '2026-02-09': {
    saturation: 1.22,
    topicScale: 1.28,
    topicPolarization: -0.28,
    answerBias: { currentGov: -18, previousAdmins: -8, globalFactors: 22, staffing: 4 },
    regions: {
      Nicosia: { sampleScale: 0.8, sentimentShift: 0.08, antiShift: -0.07, sourceReaderScale: [0.82, 0.86, 1.3, 1.04] },
      Limassol: { sampleScale: 1.06, sentimentShift: 0.11, antiShift: -0.1, sourceReaderScale: [0.8, 0.9, 1.36, 1.06] },
      Larnaca: { sampleScale: 0.9, sentimentShift: 0.12, antiShift: -0.09, sourceReaderScale: [0.84, 0.88, 1.26, 1.08] },
      Paphos: { sampleScale: 1.28, sentimentShift: 0.16, antiShift: -0.13, sourceReaderScale: [0.78, 0.84, 1.38, 1.12] }
    }
  },
  '2026-02-16': {
    saturation: 1.12,
    topicScale: 1.06,
    topicPolarization: 0.18,
    answerBias: { currentGov: 6, previousAdmins: 20, globalFactors: -14, staffing: -12 },
    regions: {
      Nicosia: { sampleScale: 0.92, sentimentShift: 0.02, antiShift: -0.02, sourceReaderScale: [0.98, 1.34, 0.82, 0.76] },
      Limassol: { sampleScale: 0.86, sentimentShift: 0.04, antiShift: -0.04, sourceReaderScale: [0.92, 1.4, 0.8, 0.74] },
      Larnaca: { sampleScale: 1.12, sentimentShift: 0.06, antiShift: -0.05, sourceReaderScale: [0.94, 1.32, 0.86, 0.78] },
      Paphos: { sampleScale: 1.22, sentimentShift: 0.08, antiShift: -0.08, sourceReaderScale: [0.88, 1.44, 0.78, 0.72] }
    }
  },
  '2026-02-23': {
    saturation: 1.32,
    topicScale: 1.36,
    topicPolarization: -0.38,
    answerBias: { currentGov: -20, previousAdmins: -16, globalFactors: 10, staffing: 28 },
    regions: {
      Nicosia: { sampleScale: 0.88, sentimentShift: 0.12, antiShift: -0.11, sourceReaderScale: [0.72, 0.8, 0.9, 1.56] },
      Limassol: { sampleScale: 0.96, sentimentShift: 0.14, antiShift: -0.13, sourceReaderScale: [0.7, 0.84, 0.92, 1.6] },
      Larnaca: { sampleScale: 1.04, sentimentShift: 0.16, antiShift: -0.12, sourceReaderScale: [0.68, 0.78, 0.94, 1.64] },
      Paphos: { sampleScale: 1.36, sentimentShift: 0.2, antiShift: -0.16, sourceReaderScale: [0.66, 0.76, 0.9, 1.72] }
    }
  }
};

const SECTION_NARRATIVES = [
  'Three poll layers capture healthcare sentiment, responsibility, and media influence.',
  'A regional snapshot reveals how people rate healthcare quality across Cyprus.',
  'Responsibility patterns diverge by region, showing where accountability is directed.',
  'A deeper breakdown shows how answer themes and news sources cluster together.',
  'Key takeaways connect this week’s patterns to plausible public-news context.'
] as const;

const OUTLETS = ['Politis', 'Phileleftheros', 'SigmaLive', 'Cyprus Mail'];

const POLL_CAMPAIGN_LAYERS: PollCampaignLayer[] = [
  {
    id: 'layer-1',
    title: 'Layer 1 · Sentiment',
    question: 'How would you rate the quality of healthcare (GeSY) in your area?',
    options: ['Excellent', 'Good', 'Average', 'Poor', 'Critical']
  },
  {
    id: 'layer-2',
    title: 'Layer 2 · Political Link',
    question: 'Who do you think is most responsible for the current healthcare situation?',
    options: ['Current Government', 'Previous Administrations', 'Global Factors', 'Staffing']
  },
  {
    id: 'layer-3',
    title: 'Layer 3 · Media Influence',
    question: 'Which portal most shapes your view on healthcare reform news?',
    options: ['Politis', 'Phileleftheros', 'SigmaLive', 'Cyprus Mail']
  }
];

const LAYER2_ANSWERS: Array<{ key: Layer2AnswerKey; label: string; sentimentShift: number }> = [
  { key: 'currentGov', label: 'Current Government', sentimentShift: -0.38 },
  { key: 'previousAdmins', label: 'Previous Administrations', sentimentShift: -0.14 },
  { key: 'globalFactors', label: 'Global Factors', sentimentShift: 0.08 },
  { key: 'staffing', label: 'Staffing', sentimentShift: -0.05 }
];

const REGION_LAYER2_SPLITS: Record<CyprusLocation, Partial<Record<Layer2AnswerKey, number>>> = {
  Nicosia: { currentGov: 75, previousAdmins: 20, staffing: 5 },
  Limassol: { currentGov: 40, staffing: 50, previousAdmins: 10 },
  Larnaca: { currentGov: 50, globalFactors: 50 },
  Paphos: { previousAdmins: 60, staffing: 40 }
};


const SVG_REGION_LABELS: Record<CyprusLocation, { x: number; y: number }> = {
  Larnaca: { x: 345, y: 317 },
  Limassol: { x: 185, y: 353 },
  Nicosia: { x: 230, y: 243 },
  Paphos: { x: 52, y: 304 }
};

const SVG_REGION_ID_MAP: Record<CyprusLocation, string> = {
  Larnaca: 'cyjs3',
  Limassol: 'cyjs4',
  Nicosia: 'cyjs5',
  Paphos: 'cyjs6'
};

const TREEMAP_CANVAS_WIDTH = 1024;
const TREEMAP_CANVAS_HEIGHT = 920;

const TEMPLATE_REGION_PATHS: Record<string, string> = {
  cyjs1:
    'M741,14.75c-2.25-0.75-3.666,2.25-7.666,1.917s-9.887,7.517-16,6.333c-5.166-1-2.334,8-8.834,7s-4.75,5.25-10.75,1.75s-8.5,8.75-19.75,6.5c-5.24-1.048-2,4-8.25,3.75s-6.5,8.75-10.5,8.75s-2.5,3.25-7.25,6.5s-8.5,0.25-10.5,3s-6.25-1.25-9.25,3.25s-11.75,2.5-18,2.25s-3.25,3.5-6.5,4.5s-8,14.5-23.25,18.5c-3.521,0.923-5.25,6.5-46.75,22.5c-26.341,10.156-39,8.5-46,8s-14.25,2.5-22.25,6.5s-4.75,7.75-23.75,13c-1.25,2.5-2.25,5,1.25,5.75s0.5,6.75-8,6.5s-16.25,10.5-21.5,10.25s-4.5,2.25-6.25,4.593c7.5,5.407-0.25,14.657,6.25,15.907S419,186,416,186s-4.57,2.25-0.035,8.25s6.433,14.472,0,18.25c-10.215,6,3.143,11,4.714,10.75s5.821,0,8.321,4c7.25-3.25,7.676-2.282,8,0c0.75,5.277,10,4,8.5,9s2.5,6.5,1.5,10.25s5.5-1.75,5.25,5s11.5,5,9.25,9.75s5,3,4.25,7.5s-5,0.75-4.5,8.75c41-1.5,39.25,6,46.25,11.25s12.75-2.25,17.75-7.5s14.75-3,18.25-6.25s13,2.5,17.25,0.25s15.25,11.5,19,8.25c1.945-1.686,4,2.75,5.75,0s-9.25-4-6-8.25s-4-2.75-8.5-16.75c-1.813-5.642-22-14.75-26.5-29.75c-1.432-4.771-7.25-5-12.25-13.328s-7.5-3.422-8.75-5.672s7.25,0,2.25-9.25c-3.084-5.706,1.5-28,6.75-31.25s1.75-11.5,11.75-15.75s7,0.25,20,0s11.725-5.109,15.5-14.25c9.5-23,64-44.5,66.75-45.25s5.076-4.139,12-11.25c9.25-9.5,23.25-10.25,27-21.75c1.397-4.285,2.75-2.5,3.75-5.5s24.5-14,28.084-16.75S723.75,39,724,36.25s12,2.5,11.5-9.25c-0.192-4.523,2.25-3,2-6.5S743.25,15.5,741,14.75z',
  cyjs2:
    'M238.75,175c5.75-3,10.94,0.721,13.5-1.573C257.75,168.5,260.5,182,264,179s15.75,2.5,21.25-1.25s10.5,0.75,14.75,0s11.75,10.25,15.25,5.75s9.5-8.25,13.75-4s12,0.5,18.25,2.75s23.5,0.25,26-3s10-14,17.5-12.25s8-3.133,11.25-0.191s7.5,1.377,9.25-0.966s1-4.843,6.25-4.593s13-10.5,21.5-10.25s11.5-5.75,8-6.5s-2.5-3.25-1.25-5.75c-9.373,2.59-13,0.25-18.5,3s-17.25,2.25-21,4S402.5,144,393.5,147s-31.5-1-36,1.5s-8.5-5-13.75-3.25s-11-3.25-13.75-1s-5.25-1.5-9.25,0s-9.5-5-20.5-3c-5.258,0.956-16.75-2.5-20.25-3.824c-5.498-2.08-10.5,7.324-23.75,1.824c-5.735-2.381-9-1.75-11.25-1.824s-17.75-8.176-25-12.926s-5.75-0.25-3.75,3.75s-2.75,9.176,3.25,14.75c3.758,3.491,1.5,20.5,1.25,28.25C232.5,170.25,233,178,238.75,175z',
  cyjs3:
    'M465.75,268.75c0.75-4.5-6.5-2.75-4.25-7.5s-9.5-3-9.25-9.75s-6.25-1.25-5.25-5s-3-5.25-1.5-10.25s-7.75-3.723-8.5-9c-0.324-2.282-0.75-3.25-8,0s-0.5,6.75-5.5,9s-14.75-7.75-21.75-3.723s1,8.973-1.75,11.723s-5.25-5-7.5,2.25s7.75,5.25,7,12s7.25,5,4,8.25S403,275.5,400,276s1.25,6.25-10,2.75c-7.775-2.419-3,4.25-8.964,4.25s-0.536-4.25-7.286-4.25s-2.75,10.5-7,13.5s-11.25-1.25-15.25,1S345,288,339,291s-3.5,2.5-2.75,6.75s-8.617,6.591-13.75,9.5c-7.5,4.25-0.75-2.5-5.5-5c-6.729-3.542-3.75,5-13.5,0c-5.702-2.924-8-2.5-11,1.75s-3,0.25-7.25,4.75s-1,7-2.25,9.602s19.5,6.398,24,6.898s7.75,14.5,18.764,16.75c4.894,1,1.486,6,3.236,12.75s-6.75,10-1.5,16.5c4.5-6,15,0.5,16-1.75s4.25,1,6.5-2s4.75,0.25,9.5-4.25s13-4.4,20.5-10.75c5.277-4.468,17.25-2.75,18.75-6s4.75,1.25,5.75-2.75s4.25-1.75,7.25-5.899s9.5-3.351,15.75-3.101s5.25-0.75,6-5.75s10.337-10.497,9.07-13.25c-2.07-4.5,0.585-2.281,0.18-10.75c-0.544-11.372,9.667-27.177,18.5-27.5C460.75,269.5,465,273.25,465.75,268.75z',
  cyjs4:
    'M329,354.75c-1.75-6.75,1.658-11.75-3.236-12.75C314.75,339.75,311.5,325.75,307,325.25s-25.25-4.296-24-6.898s-2-5.102,2.25-9.602c-5.75-1-12.25-8.5-18-3.25s-6.375,1.596-8.75-1.848C251.5,293.5,241,291.5,240,288.25s-2.52-1.815-8-2.5c-12-1.5-14.75,6-18.75,5.5s-9.5,2.75-14.25-0.5s-19.588-12.229-20.25-9.25c-1.5,6.75-7.75,2.75-7.75,7.25s-1.75,2.75-1.75,12.75s9.75,7.75,5.5,12.25s-12.845,11.446-15.5,16.25c-5.25,9.5-12.75,7.75-11.75,14.5s7,1.75,7.5,6.5s-4,3-4.75,6.75s-10.25,3-10.75,7.5s-6.583,0.48-6.75,6c-0.25,8.25-5.25-3.5-4,20.5c3.75,1.75,12.25,4.5,16.75,6.75s7.75-3.25,10-1.587s4.25-0.413,8.75-3.163s9.5-4.5,13-2.75s8-3.75,10.25-2s14.5-1.5,17.5,2.25s5.5,1.75,5.75,5.663s6.25,4.087,6,9.337s8,14,5,16.25s-0.25,3.75,2.75,2s3.25,2.25,7.25-1s5.75,0.75,8.75-0.5s6,4.5,9.5,3.75s3.25-1.25-3.5-10.902c-6.344-9.072-2-13.598-1-18.935s6.75-5.163,9-8.413s8-6.25,17.5-11.65s25.25-3.35,39.5-1.35s11.5,1.75,16-4.25C322.25,364.75,330.75,361.5,329,354.75z',
  cyjs5:
    'M119.5,220.724c0.25,6.526,7.5,2.776,7.25,9.776s2.25,9.25-3,11.25s-6,3.75-1.5,6s-0.4,8.5,6.3,9.25s0.95,4.25,7.95,7.5s1.71,6.815,2.75,10.75c3.5,13.25-5,13.25-3.25,18.25s-3.75,6.25,1.75,7.75s1.5,4.75,6.25,5.25s0-5.5,4-5.5s0.25,6.56,5,5.53s2.25-9.03,8-9.53s2.25,4.75,8.25,4.5c0-10,1.75-8.25,1.75-12.75s6.25-0.5,7.75-7.25c0.662-2.979,15.5,6,20.25,9.25s10.25,0,14.25,0.5s6.75-7,18.75-5.5c5.48,0.685,7-0.75,8,2.5s11.5,5.25,18.5,15.402c2.375,3.444,3,7.098,8.75,1.848s12.25,2.25,18,3.25c4.25-4.5,4.25-0.5,7.25-4.75s5.298-4.674,11-1.75c9.75,5,6.771-3.542,13.5,0c4.75,2.5-2,9.25,5.5,5c5.133-2.909,14.5-5.25,13.75-9.5S333,294,339,291s8.5,4.5,12.5,2.25s11,2,15.25-1s0.25-13.5,7-13.5s1.322,4.25,7.286,4.25s1.188-6.669,8.964-4.25c11.25,3.5,7-2.25,10-2.75s0.25-6,3.5-9.25s-4.75-1.5-4-8.25s-9.25-4.75-7-12s4.75,0.5,7.5-2.25s-5.25-7.696,1.75-11.723s16.75,5.973,21.75,3.723s-1.75-5.75,5.5-9c-2.5-4-6.75-4.25-8.321-4s-14.929-4.75-4.714-10.75c6.433-3.778,4.535-12.25,0-18.25S413,186,416,186s8-3,1.5-4.25s1.25-10.5-6.25-15.907c-1.75,2.343-6,3.907-9.25,0.966s-3.75,1.941-11.25,0.191s-15,9-17.5,12.25s-19.75,5.25-26,3s-14,1.5-18.25-2.75s-10.25-0.5-13.75,4s-11-6.5-15.25-5.75s-9.25-3.75-14.75,0S267.5,176,264,179s-6.25-10.5-11.75-5.573c-2.56,2.293-7.75-1.427-13.5,1.573s-6.25-4.75-18-3.75c-0.25,7.75-6.75,23.5-8.75,29.5s-14.25,13.25-17.25,14.672S180,218.25,175,213.25s-9.75-3.5-12.5-6.5s-6.5-1.5-10.5-4.25s-5.5,0-15.25-3.25s-9.25,1.75-14.25,0.25s-4.5,5-8,4.75s-1.5,1.25-7.25,3C112.25,215.75,119.25,214.197,119.5,220.724z',
  cyjs6:
    'M132.75,371.25c0.167-5.52,6.25-1.5,6.75-6s10-3.75,10.75-7.5s5.25-2,4.75-6.75s-6.5,0.25-7.5-6.5s6.5-5,11.75-14.5c2.655-4.804,11.25-11.75,15.5-16.25s-5.5-2.25-5.5-12.25c-6,0.25-2.5-5-8.25-4.5s-3.25,8.5-8,9.53s-1-5.53-5-5.53s0.75,6-4,5.5s-0.75-3.75-6.25-5.25s0-2.75-1.75-7.75s6.75-5,3.25-18.25c-1.04-3.935,4.25-7.5-2.75-10.75s-1.25-6.75-7.95-7.5s-1.8-7-6.3-9.25s-3.75-4,1.5-6s2.75-4.25,3-11.25s-7-3.25-7.25-9.776s-7.25-4.974-12.25-13.474c-5.75,1.75-4.5-3.75-8.75-2.25s3.5,6-5,10.422s-15.25,36.328-40,38.828s-34.5-22-40.25-21.723s-3,6.723-3.5,13.973s18.5,27.419,12.25,37c-3.482,5.338,11,12.25,3.5,20.25c-4.729,5.044,7,7.25,7.25,14.602C32.912,323.118,47.25,322.5,47,327s3.25,9.75,3.25,15s3.25,1,2.25,6.25s-1.25,9.25,3.5,9.25s6.5,6.75,9,6.5s13,10.25,15.5,11.75s4.25-5,9-0.75s14.5,1.25,21.25,8s14.25,7,18,8.75C127.5,367.75,132.5,379.5,132.75,371.25z'
};

const normalize = (value: string) => value.toLowerCase().replace(/[^a-z]/g, '');

const clamp = (value: number, min: number, max: number) => Math.max(min, Math.min(max, value));

const scoreToColor = (score: number, saturation = 1) => {
  const bounded = clamp(score, -1, 1);
  const hue = 120 * ((bounded + 1) / 2);
  const sat = clamp(72 * saturation, 20, 90);
  const light = bounded > 0 ? 43 : 48;
  return `hsl(${hue.toFixed(0)} ${sat.toFixed(0)}% ${light}%)`;
};

const parseColorToRgb = (value: string) => {
  const trimmed = value.trim();
  const rgbMatch = trimmed.match(
    /^rgba?\(\s*([0-9]{1,3})\s*[, ]\s*([0-9]{1,3})\s*[, ]\s*([0-9]{1,3})/i
  );
  if (rgbMatch) {
    return {
      r: clamp(Number(rgbMatch[1]), 0, 255),
      g: clamp(Number(rgbMatch[2]), 0, 255),
      b: clamp(Number(rgbMatch[3]), 0, 255)
    };
  }

  const safeHex = trimmed.replace('#', '');
  const expanded = safeHex.length === 3 ? safeHex.split('').map((c) => `${c}${c}`).join('') : safeHex;
  const num = Number.parseInt(expanded, 16);
  if (!Number.isFinite(num)) {
    return { r: 0, g: 0, b: 0 };
  }
  return {
    r: (num >> 16) & 255,
    g: (num >> 8) & 255,
    b: num & 255
  };
};

const interpolateColor = (startColor: string, endColor: string, amount: number) => {
  const start = parseColorToRgb(startColor);
  const end = parseColorToRgb(endColor);
  const t = clamp(amount, 0, 1);
  const r = Math.round(start.r + (end.r - start.r) * t);
  const g = Math.round(start.g + (end.g - start.g) * t);
  const b = Math.round(start.b + (end.b - start.b) * t);
  return `rgb(${r}, ${g}, ${b})`;
};

const pollScoreFromSentiment = (sentiment: number) => {
  const bounded = clamp(sentiment, -1, 1);
  return 1 + ((bounded + 1) / 2) * 4;
};

const pollScoreToColor = (pollScore: number) => {
  const t = (clamp(pollScore, 1, 5) - 1) / 4;
  return interpolateColor('#ef4444', '#22c55e', t);
};

const rectPath = (x0: number, y0: number, x1: number, y1: number) =>
  `M${x0},${y0} L${x1},${y0} L${x1},${y1} L${x0},${y1} Z`;

const truncateLabel = (value: string, maxChars: number) =>
  value.length > maxChars ? `${value.slice(0, Math.max(3, maxChars - 1))}…` : value;

const wrapLabelLines = (value: string, maxCharsPerLine: number, maxLines: number) => {
  const safeChars = Math.max(3, maxCharsPerLine);
  const safeLines = Math.max(1, maxLines);
  const words = value.trim().split(/\s+/).filter(Boolean);
  if (words.length === 0) return [''];

  const lines: string[] = [];
  let current = '';

  words.forEach((word) => {
    if (word.length > safeChars) {
      const chunked = word.match(new RegExp(`.{1,${safeChars}}`, 'g')) ?? [word];
      chunked.forEach((chunk) => {
        if (current.length > 0) {
          lines.push(current);
          current = '';
        }
        lines.push(chunk);
      });
      return;
    }
    const next = current.length > 0 ? `${current} ${word}` : word;
    if (next.length <= safeChars) {
      current = next;
    } else {
      lines.push(current);
      current = word;
    }
  });

  if (current.length > 0) {
    lines.push(current);
  }

  const trimmed = lines.slice(0, safeLines);
  if (lines.length > safeLines) {
    const last = trimmed[trimmed.length - 1] ?? '';
    trimmed[trimmed.length - 1] = truncateLabel(last, safeChars);
  }
  return trimmed;
};

const formatWeekRange = (startDateStr: string) => {
  const start = new Date(`${startDateStr}T00:00:00Z`);
  const end = new Date(start);
  end.setUTCDate(start.getUTCDate() + 6);
  const format = (date: Date) =>
    date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC' });
  return `${format(start)} - ${format(end)}`;
};

const extractTopics = (records: TopicRecord[], location: CyprusLocation): string[] => {
  const key = normalize(location);
  const locationRows = records.filter((row) => normalize(row.location).includes(key));
  const rows = locationRows.length > 0 ? locationRows : records;
  const found = rows.flatMap((row) => row.topics).filter(Boolean);
  return Array.from(new Set(found)).slice(0, 6);
};

function InfographicPage({ data, week, onBack }: InfographicPageProps) {
  const mapSankeySectionRef = useRef<HTMLElement | null>(null);
  const shatterStageRef = useRef<HTMLDivElement | null>(null);
  const shatterNodeRef = useRef<HTMLDivElement | null>(null);
  const sourceMorphRefs = useRef<Record<string, SVGPathElement | null>>({});
  const flowLinkRefs = useRef<Record<string, SVGPathElement | null>>({});
  const [hoveredRegion, setHoveredRegion] = useState<CyprusLocation | null>(null);
  const [selectedRegion, setSelectedRegion] = useState<CyprusLocation>('Nicosia');
  const [weekIndex, setWeekIndex] = useState(() => {
    const fallbackIndex = INFOGRAPHIC_WEEKS.indexOf('2026-01-26');
    const incomingIndex = INFOGRAPHIC_WEEKS.indexOf(week as InfographicWeek);
    return incomingIndex >= 0 ? incomingIndex : fallbackIndex;
  });
  const [prefersReducedMotion, setPrefersReducedMotion] = useState(false);
  const [campaignMapProgress, setCampaignMapProgress] = useState(0);
  const [mapSankeyProgress, setMapSankeyProgress] = useState(0);
  const [sankeyTreemapProgress, setSankeyTreemapProgress] = useState(0);
  const [treemapTakeawayProgress, setTreemapTakeawayProgress] = useState(0);
  const [shatterExpanded, setShatterExpanded] = useState(false);

  const selectedWeek = INFOGRAPHIC_WEEKS[clamp(weekIndex, 0, INFOGRAPHIC_WEEKS.length - 1)] as InfographicWeek;
  const weeklyProfile = WEEKLY_PROFILES[selectedWeek];

  useEffect(() => {
    const incomingIndex = INFOGRAPHIC_WEEKS.indexOf(week as InfographicWeek);
    if (incomingIndex >= 0) {
      setWeekIndex(incomingIndex);
    }
  }, [week]);

  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    const applyPreference = () => setPrefersReducedMotion(mediaQuery.matches);
    applyPreference();
    mediaQuery.addEventListener('change', applyPreference);
    return () => mediaQuery.removeEventListener('change', applyPreference);
  }, []);

  const storyData = useMemo<LocationStory[]>(() => {
    const allTopicRecords = [
      ...data.topics.political.Left,
      ...data.topics.political.Neutral,
      ...data.topics.political.Right
    ];

    const objectivityByLocation = data.objectivity.political.reduce<Record<string, number[]>>((acc, row) => {
      if (!row.location) return acc;
      const key = normalize(row.location);
      if (!acc[key]) acc[key] = [];
      acc[key].push(row.avgObjectivity);
      return acc;
    }, {});

    return CYPRUS_LOCATIONS.map((location, index) => {
      const locationKey = normalize(location);
      const liveRow =
        data.locationStacks.find((entry) => normalize(entry.location).includes(locationKey)) ??
        data.locationStacks[index];

      const liveReaders = liveRow?.total ?? 1000 + index * 250;
      const locationObjectivity = objectivityByLocation[locationKey] ?? [];
      const objectivityAvg =
        locationObjectivity.length > 0
          ? locationObjectivity.reduce((sum, value) => sum + value, 0) / locationObjectivity.length
          : 0.5;

      const biasByLocation: Record<CyprusLocation, number> = {
        Nicosia: -0.05,
        Limassol: -0.22,
        Larnaca: -0.08,
        Paphos: 0.1
      };

      const sentiment = clamp((objectivityAvg - 0.5) * 1.5 + biasByLocation[location], -1, 1);
      const healthTemperature = Math.round(clamp((sentiment + 1) * 50, 8, 96));

      const blameCurrentGov = clamp(34 + (1 - (sentiment + 1) / 2) * 44 + index * 2, 18, 82);
      const blameOthers = 100 - blameCurrentGov;

      const sources = OUTLETS.map((outlet, sourceIndex) => {
        const outletWeight = [0.34, 0.28, 0.22, 0.16][sourceIndex];
        const sentimentShift = [0.18, -0.08, -0.2, 0.05][sourceIndex];
        return {
          outlet,
          readers: Math.round(liveReaders * outletWeight),
          sentiment: clamp(sentiment + sentimentShift, -1, 1)
        };
      });

      const topics = extractTopics(allTopicRecords, location);

      return {
        location,
        totalReaders: liveReaders,
        healthTemperature,
        sentiment,
        blameCurrentGov,
        blameOthers,
        proWeight: blameOthers / 100,
        antiWeight: blameCurrentGov / 100,
        sources,
        topics
      };
    });
  }, [data]);

  const weeklyScrollyDataByWeek = useMemo<Record<InfographicWeek, ScrollyPayload>>(() => {
    const buildWeekPayload = (profile: WeeklyProfile): ScrollyPayload => {
      const weekAdjustedStories = storyData.map((row) => {
        const regionProfile = profile.regions[row.location];
        const antiWeightRaw = clamp(
          row.antiWeight + regionProfile.antiShift + profile.answerBias.currentGov * 0.003,
          0.08,
          0.92
        );
        const proWeightRaw = clamp(
          row.proWeight - regionProfile.antiShift + (profile.answerBias.globalFactors + profile.answerBias.staffing) * 0.0015,
          0.08,
          0.92
        );
        const normalizedTotal = antiWeightRaw + proWeightRaw || 1;
        const antiWeight = antiWeightRaw / normalizedTotal;
        const proWeight = proWeightRaw / normalizedTotal;
        const sentiment = clamp(row.sentiment + regionProfile.sentimentShift, -1, 1);
        const readersBase = row.totalReaders * regionProfile.sampleScale;

        return {
          ...row,
          totalReaders: Math.round(readersBase),
          sentiment,
          healthTemperature: Math.round(clamp((sentiment + 1) * 50, 4, 98)),
          proWeight,
          antiWeight,
          sources: row.sources.map((source, index) => {
            const sourceScale = regionProfile.sourceReaderScale[index] ?? 1;
            const sourceWave = [0.07, -0.03, -0.07, 0.03][index] ?? 0;
            const topicWave = profile.topicPolarization * ([-0.15, -0.05, 0.08, 0.18][index] ?? 0);
            return {
              ...source,
              readers: Math.round(source.readers * regionProfile.sampleScale * sourceScale),
              sentiment: clamp(
                source.sentiment + regionProfile.sentimentShift * 0.9 + sourceWave * 0.35 + topicWave,
                -1,
                1
              )
            };
          })
        };
      });

      const regions: RegionSchema[] = weekAdjustedStories.map((row) => ({
        id: row.location,
        name: row.location,
        avgSentiment: row.sentiment,
        politicalWeight: row.antiWeight,
        healthTemperature: row.healthTemperature,
        sample: row.totalReaders,
        proWeight: row.proWeight,
        antiWeight: row.antiWeight,
        sources: row.sources,
        topics: row.topics
      }));

      const nodes: SankeyNodeSchema[] = [
        ...regions.map((region) => ({ id: region.id, name: region.name, group: 'region' as const })),
        { id: 'antiGov', name: 'Blame Current Gov', group: 'target' },
        { id: 'proGov', name: 'Blame Others', group: 'target' }
      ];

      const links: SankeyLinkSchema[] = regions.flatMap((region) => [
        {
          source: region.id,
          target: 'antiGov',
          value: Math.round(region.sample * region.antiWeight)
        },
        {
          source: region.id,
          target: 'proGov',
          value: Math.round(region.sample * region.proWeight)
        }
      ]);

      const treemapData: TreemapSchema = {
        name: 'Healthcare Status Responsibility',
        children: OUTLETS.map((outlet, outletIndex) => {
          const answerBuckets = new Map<Layer2AnswerKey, { value: number; sentimentWeightedSum: number }>();
          LAYER2_ANSWERS.forEach((answer) => {
            answerBuckets.set(answer.key, { value: 0, sentimentWeightedSum: 0 });
          });

          regions.forEach((region) => {
            const sourceData = region.sources.find((source) => source.outlet === outlet);
            if (!sourceData) return;

            const configuredSplit = REGION_LAYER2_SPLITS[region.id] ?? {};
            const rawShares: Record<Layer2AnswerKey, number> = {
              currentGov: configuredSplit.currentGov ?? 0,
              previousAdmins: configuredSplit.previousAdmins ?? 0,
              globalFactors: configuredSplit.globalFactors ?? 0,
              staffing: configuredSplit.staffing ?? 0
            };

            const biasedShares = Object.fromEntries(
              LAYER2_ANSWERS.map((answer) => [
                answer.key,
                Math.max(2, (rawShares[answer.key] ?? 0) + profile.answerBias[answer.key])
              ])
            ) as Record<Layer2AnswerKey, number>;

            const totalRaw = Object.values(biasedShares).reduce((sum, value) => sum + value, 0) || 1;
            const normalizedShares = Object.fromEntries(
              Object.entries(biasedShares).map(([key, value]) => [key, value / totalRaw])
            ) as Record<Layer2AnswerKey, number>;

            LAYER2_ANSWERS.forEach((answer, answerIndex) => {
              const outletWave = 1 + profile.topicPolarization * ([0.12, 0.04, -0.06, -0.1][outletIndex] ?? 0);
              const answerWave = 1 + profile.topicPolarization * ([0.16, 0.05, -0.08, -0.04][answerIndex] ?? 0);
              const weightedValue = sourceData.readers * normalizedShares[answer.key] * outletWave * answerWave;
              const compressedValue = Math.pow(Math.max(weightedValue, 1), 0.9);
              const minimumVisibleTile = 7;
              const bucket = answerBuckets.get(answer.key);
              if (!bucket) return;
              const value = Math.max(1, Math.round(minimumVisibleTile + compressedValue));
              bucket.value += value;
              bucket.sentimentWeightedSum +=
                clamp(sourceData.sentiment + answer.sentimentShift, -1, 1) * value;
            });
          });

          return {
            name: outlet,
            children: LAYER2_ANSWERS.map((answer) => ({
              name: answer.label,
              value: Math.max(1, Math.round(answerBuckets.get(answer.key)?.value ?? 1)),
              sentiment:
                (answerBuckets.get(answer.key)?.sentimentWeightedSum ?? 0) /
                Math.max(answerBuckets.get(answer.key)?.value ?? 1, 1)
            }))
          };
        })
      };

      return {
        regions,
        sankeyData: { nodes, links },
        treemapData
      };
    };

    return Object.fromEntries(
      INFOGRAPHIC_WEEKS.map((weekKey) => [weekKey, buildWeekPayload(WEEKLY_PROFILES[weekKey])])
    ) as Record<InfographicWeek, ScrollyPayload>;
  }, [storyData]);

  const scrollyData = weeklyScrollyDataByWeek[selectedWeek];

  useEffect(() => {
    if (!scrollyData.regions.some((region) => region.id === selectedRegion)) {
      setSelectedRegion(scrollyData.regions[0]?.id ?? 'Nicosia');
    }
  }, [scrollyData.regions, selectedRegion]);

  const summary = useMemo(() => {
    const avgTemp = Math.round(
      scrollyData.regions.reduce((sum, region) => sum + region.healthTemperature, 0) /
        Math.max(scrollyData.regions.length, 1)
    );
    const avgBlame =
      scrollyData.regions.reduce((sum, region) => sum + region.antiWeight * 100, 0) /
      Math.max(scrollyData.regions.length, 1);
    const tempRanked = [...scrollyData.regions].sort((a, b) => b.healthTemperature - a.healthTemperature);
    const warmestRegion = tempRanked[0]?.name ?? 'Nicosia';
    const coolestRegion = tempRanked[tempRanked.length - 1]?.name ?? 'Paphos';

    const antiLinksTotal = scrollyData.sankeyData.links
      .filter((link) => link.target === 'antiGov')
      .reduce((sum, link) => sum + link.value, 0);
    const proLinksTotal = scrollyData.sankeyData.links
      .filter((link) => link.target === 'proGov')
      .reduce((sum, link) => sum + link.value, 0);
    const totalFlow = Math.max(antiLinksTotal + proLinksTotal, 1);
    const blameGap = Math.round(((antiLinksTotal - proLinksTotal) / totalFlow) * 100);

    const sourceStats = new Map<string, { value: number; sentimentWeighted: number }>();
    scrollyData.regions.forEach((region) => {
      region.sources.forEach((source) => {
        const entry = sourceStats.get(source.outlet) ?? { value: 0, sentimentWeighted: 0 };
        entry.value += source.readers;
        entry.sentimentWeighted += source.sentiment * source.readers;
        sourceStats.set(source.outlet, entry);
      });
    });

    const topSource =
      [...sourceStats.entries()].sort((a, b) => b[1].value - a[1].value)[0]?.[0] ?? OUTLETS[0];
    const mostCriticalSource =
      [...sourceStats.entries()]
        .map(([outlet, stats]) => [outlet, stats.sentimentWeighted / Math.max(stats.value, 1)] as const)
        .sort((a, b) => a[1] - b[1])[0]?.[0] ?? OUTLETS[0];

    const answerTotals = new Map<string, number>();
    scrollyData.treemapData.children?.forEach((sourceNode) => {
      sourceNode.children?.forEach((answerNode) => {
        answerTotals.set(answerNode.name, (answerTotals.get(answerNode.name) ?? 0) + (answerNode.value ?? 0));
      });
    });
    const dominantAnswer =
      [...answerTotals.entries()].sort((a, b) => b[1] - a[1])[0]?.[0] ?? 'Current Government';

    return {
      avgTemp,
      avgBlame: Math.round(avgBlame),
      warmestRegion,
      coolestRegion,
      blameGap,
      topSource,
      mostCriticalSource,
      dominantAnswer
    };
  }, [scrollyData.regions, scrollyData.sankeyData.links, scrollyData.treemapData.children]);

  const takeawayRows = useMemo<TakeawayRow[]>(
    () => [
      {
        signal: 'Regional Temperature Spread',
        whatChanged: `${summary.warmestRegion} now leads the health sentiment map, while ${summary.coolestRegion} remains the coolest zone this week. National health temperature sits at ${summary.avgTemp}/100.`,
        possibleDriver: 'a rapid clinic staffing announcement improved confidence in urban centers, while smaller districts reported longer GP waiting lines.',
        whyItMatters: 'This gap explains why national averages hide strong local differences in patient mood.'
      },
      {
        signal: 'Responsibility Flow (Sankey)',
        whatChanged: `Average blame on current government is ${summary.avgBlame}% and ${Math.abs(summary.blameGap)}% net flow now leans ${
          summary.blameGap >= 0 ? 'toward blaming the current government' : 'toward blaming external/structural factors'
        }.`,
        possibleDriver: 'debate around emergency department congestion and funding reallocations dominated prime-time headlines.',
        whyItMatters: 'Attribution direction usually predicts whether sentiment stabilizes or keeps fragmenting in following weeks.'
      },
      {
        signal: 'Outlet Weight Shift',
        whatChanged: `${summary.topSource} is the largest source block this week, while ${summary.mostCriticalSource} carries the most critical tone among major outlets.`,
        possibleDriver: 'exclusive reports on hospital procurement and contract delays increased readership concentration in specific outlets.',
        whyItMatters: 'When a single source grows in share, it can amplify one framing of healthcare performance.'
      },
      {
        signal: 'Answer Mix In Treemap',
        whatChanged: `"${summary.dominantAnswer}" is currently the largest responsibility answer across sources in ${formatWeekRange(selectedWeek)}.`,
        possibleDriver: 'a two-week cycle of reform promises versus opposition rebuttals kept responsibility narratives highly salient.',
        whyItMatters: 'The dominant answer indicates which policy narrative is shaping public interpretation of health-system outcomes.'
      }
    ],
    [selectedWeek, summary.avgBlame, summary.avgTemp, summary.blameGap, summary.coolestRegion, summary.dominantAnswer, summary.mostCriticalSource, summary.topSource, summary.warmestRegion]
  );

  const activeRegion = scrollyData.regions.find((region) => region.id === (hoveredRegion ?? selectedRegion));

  const liveNarrative = useMemo(() => {
    const narrativeIndex =
      campaignMapProgress < 0.5 ? 0 : mapSankeyProgress < 0.5 ? 1 : sankeyTreemapProgress < 0.5 ? 2 : treemapTakeawayProgress < 0.5 ? 3 : 4;
    const sectionSummary = SECTION_NARRATIVES[narrativeIndex] ?? SECTION_NARRATIVES[0];
    if (!activeRegion || narrativeIndex === 0 || narrativeIndex === 4) {
      return sectionSummary;
    }
    return `${sectionSummary} Focus region ${activeRegion.name}: health temperature ${activeRegion.healthTemperature} out of 100.`;
  }, [activeRegion, campaignMapProgress, mapSankeyProgress, sankeyTreemapProgress, treemapTakeawayProgress]);

  const nameTransferProgress = clamp((mapSankeyProgress - 0.38) / 0.24, 0, 1);
  const mapEntryOpacity = clamp((campaignMapProgress - 0.15) / 0.7, 0, 1);
  const campaignLayerOpacity = 1 - campaignMapProgress;
  const mapLabelOpacity = (1 - nameTransferProgress) * mapEntryOpacity;
  const sankeyLabelOpacity =
    nameTransferProgress * (1 - clamp((sankeyTreemapProgress - 0.1) / 0.25, 0, 1)) * mapEntryOpacity;
  const mapUiOpacity = (1 - nameTransferProgress) * mapEntryOpacity;
  const mapSankeyLayerOpacity = (1 - clamp((sankeyTreemapProgress - 0.12) / 0.35, 0, 1)) * mapEntryOpacity;
  const treemapLayerOpacity = clamp((sankeyTreemapProgress - 0.06) / 0.4, 0, 1);
  const treemapExitProgress = clamp((treemapTakeawayProgress - 0.08) / 0.4, 0, 1);
  const treemapVisibleOpacity = treemapLayerOpacity * (1 - treemapExitProgress);
  const takeawayLayerOpacity = clamp((treemapTakeawayProgress - 0.1) / 0.42, 0, 1);

  const treemapLayoutByWeek = useMemo<Record<InfographicWeek, SectionTreemapLayout>>(() => {
    return Object.fromEntries(
      INFOGRAPHIC_WEEKS.map((weekKey) => {
        const root = hierarchy(weeklyScrollyDataByWeek[weekKey].treemapData)
          .sum((node) => node.value ?? 0)
          .sort((a, b) => (b.value ?? 0) - (a.value ?? 0));

        const rectRoot = d3Treemap<TreemapSchema>()
          .size([TREEMAP_CANVAS_WIDTH, TREEMAP_CANVAS_HEIGHT])
          .paddingOuter(8)
          .paddingTop((node) => {
            if (node.depth === 0) return 32;
            if (node.depth === 1) return 34;
            if (node.depth === 2) return 18;
            return 0;
          })
          .paddingInner((node) => {
            if (node.depth <= 1) return 8;
            if (node.depth === 2) return 7;
            return 5;
          })
          .round(true)(root);

        const answers = rectRoot
          .descendants()
          .filter((node) => node.depth === 2) as HierarchyRectangularNode<TreemapSchema>[];

        return [
          weekKey,
          {
            sources: (rectRoot.children ?? []) as HierarchyRectangularNode<TreemapSchema>[],
            answers
          }
        ];
      })
    ) as Record<InfographicWeek, SectionTreemapLayout>;
  }, [weeklyScrollyDataByWeek]);

  const sectionTreemapLayout = treemapLayoutByWeek[selectedWeek];

  const sankeyLayoutByWeek = useMemo<
    Record<InfographicWeek, { sourceNodes: SankeyNodeLayout[]; targetNodes: SankeyNodeLayout[]; links: SankeyLinkLayout[] }>
  >(() => {
    return Object.fromEntries(
      INFOGRAPHIC_WEEKS.map((weekKey) => {
        const graph = sankey<SankeyGraphNode, SankeyGraphLink>()
          .nodeId((node) => node.id)
          .nodeWidth(20)
          .nodePadding(40)
          .extent([
            [0, 0],
            [750, 450]
          ])({
          nodes: weeklyScrollyDataByWeek[weekKey].sankeyData.nodes.map((node) => ({ ...node })),
          links: weeklyScrollyDataByWeek[weekKey].sankeyData.links.map((link) => ({ ...link }))
        });

        const sourceNodes: SankeyNodeLayout[] = graph.nodes
          .filter((node) => node.group === 'region')
          .map((node) => ({
            id: node.id,
            name: node.name,
            group: 'region',
            x0: node.x0 ?? 0,
            x1: node.x1 ?? 0,
            y0: node.y0 ?? 0,
            y1: node.y1 ?? 0
          }));

        const targetNodes: SankeyNodeLayout[] = graph.nodes
          .filter((node) => node.group === 'target')
          .map((node) => ({
            id: node.id,
            name: node.name,
            group: 'target',
            x0: node.x0 ?? 0,
            x1: node.x1 ?? 0,
            y0: node.y0 ?? 0,
            y1: node.y1 ?? 0
          }));

        const linkPath = sankeyLinkHorizontal<SankeyGraphNode, SankeyGraphLink>();
        const links: SankeyLinkLayout[] = graph.links.map((link) => {
          const sourceRef = link.source as SankeyGraphNode | string | number;
          const targetRef = link.target as SankeyGraphNode | string | number;
          const sourceId =
            typeof sourceRef === 'object' && sourceRef !== null
              ? String(sourceRef.id)
              : String(sourceRef);
          const targetId =
            typeof targetRef === 'object' && targetRef !== null
              ? String(targetRef.id)
              : String(targetRef);

          return {
            source: sourceId,
            target: targetId,
            value: link.value ?? 0,
            id: `${sourceId}->${targetId}`,
            path: linkPath(link) ?? '',
            width: Math.max(link.width ?? 0, 2)
          };
        });

        return [weekKey, { sourceNodes, targetNodes, links }];
      })
    ) as Record<
      InfographicWeek,
      { sourceNodes: SankeyNodeLayout[]; targetNodes: SankeyNodeLayout[]; links: SankeyLinkLayout[] }
    >;
  }, [weeklyScrollyDataByWeek]);

  const sankeyLayout = sankeyLayoutByWeek[selectedWeek];

  const shatterSlotStyle = useMemo<CSSProperties>(() => {
    const fallback: CSSProperties = {
      left: '90%',
      top: '28%',
      width: '6%',
      height: '46%',
      position: 'absolute'
    };

    if (sankeyLayout.targetNodes.length === 0) return fallback;
    const x0 = Math.min(...sankeyLayout.targetNodes.map((node) => node.x0));
    const y0 = Math.min(...sankeyLayout.targetNodes.map((node) => node.y0));
    const x1 = Math.max(...sankeyLayout.targetNodes.map((node) => node.x1));
    const y1 = Math.max(...sankeyLayout.targetNodes.map((node) => node.y1));

    return {
      left: `${(x0 / 750) * 100}%`,
      top: `${(y0 / 450) * 100}%`,
      width: `${((x1 - x0) / 750) * 100}%`,
      height: `${((y1 - y0) / 450) * 100}%`,
      position: 'absolute'
    };
  }, [sankeyLayout.targetNodes]);

  useLayoutEffect(() => {
    const section = mapSankeySectionRef.current;
    if (!section || sankeyLayout.sourceNodes.length === 0) return;

    gsap.registerPlugin(ScrollTrigger, Flip);

    const sourceMorphs = sankeyLayout.sourceNodes.map((sourceNode) => {
      const region = scrollyData.regions.find((entry) => entry.id === sourceNode.id);
      const svgId = SVG_REGION_ID_MAP[sourceNode.id as CyprusLocation];
      const fromPath = TEMPLATE_REGION_PATHS[svgId];
      const toPath = rectPath(sourceNode.x0, sourceNode.y0, sourceNode.x1, sourceNode.y1);
      const mapColor = region
        ? pollScoreToColor(pollScoreFromSentiment(region.avgSentiment))
        : '#d9dde4';
      const politicalColor =
        region && region.antiWeight >= region.proWeight ? '#f05454' : '#3ddc84';

      return {
        id: sourceNode.id,
        interpolator: flubberInterpolate(fromPath, toPath, { maxSegmentLength: 2 }),
        fromColor: mapColor,
        toColor: politicalColor
      };
    });

    // Reset to initial state before ScrollTrigger starts.
    sourceMorphs.forEach((morph) => {
      const path = sourceMorphRefs.current[morph.id];
      if (!path) return;
      path.setAttribute('d', morph.interpolator(0));
      path.setAttribute('fill', morph.fromColor);
    });
    setCampaignMapProgress(0);
    setMapSankeyProgress(0);
    setSankeyTreemapProgress(0);
    setTreemapTakeawayProgress(0);
    setShatterExpanded(false);
    sankeyLayout.links.forEach((link) => {
      const path = flowLinkRefs.current[link.id];
      if (!path) return;
      path.style.opacity = '0';
      path.style.strokeDasharray = '1';
      path.style.strokeDashoffset = '1';
    });

    if (prefersReducedMotion) {
      sourceMorphs.forEach((morph) => {
        const path = sourceMorphRefs.current[morph.id];
        if (!path) return;
        path.setAttribute('d', morph.interpolator(1));
        path.setAttribute('fill', morph.toColor);
      });
      setCampaignMapProgress(1);
      setMapSankeyProgress(1);
      setSankeyTreemapProgress(1);
      setTreemapTakeawayProgress(1);
      setShatterExpanded(true);
      sankeyLayout.links.forEach((link) => {
        const path = flowLinkRefs.current[link.id];
        if (!path) return;
        path.style.opacity = '0';
        path.style.strokeDashoffset = '0';
      });
      return;
    }

    const progressProxy = { value: 0 };
    let lastCampaignProgress = -1;
    let lastMapProgress = -1;
    let lastShatterProgress = -1;
    let lastTakeawayProgress = -1;
    const tween = gsap.to(progressProxy, {
      value: 1,
      ease: 'power1.inOut',
      scrollTrigger: {
        trigger: section,
        start: 'top top',
        end: 'bottom bottom',
        scrub: true
      },
      onUpdate: () => {
        const raw = clamp(progressProxy.value, 0, 1);
        const campaignProgress = clamp((raw - 0.06) / 0.18, 0, 1);
        const mapProgress = clamp((raw - 0.32) / 0.22, 0, 1);
        const shatterProgress = clamp((raw - 0.58) / 0.22, 0, 1);
        const takeawayProgress = clamp((raw - 0.84) / 0.12, 0, 1);

        if (Math.abs(campaignProgress - lastCampaignProgress) > 0.01) {
          lastCampaignProgress = campaignProgress;
          setCampaignMapProgress(campaignProgress);
        }
        if (Math.abs(mapProgress - lastMapProgress) > 0.01) {
          lastMapProgress = mapProgress;
          setMapSankeyProgress(mapProgress);
        }
        if (Math.abs(shatterProgress - lastShatterProgress) > 0.01) {
          lastShatterProgress = shatterProgress;
          setSankeyTreemapProgress(shatterProgress);
        }
        if (Math.abs(takeawayProgress - lastTakeawayProgress) > 0.01) {
          lastTakeawayProgress = takeawayProgress;
          setTreemapTakeawayProgress(takeawayProgress);
        }
        sourceMorphs.forEach((morph) => {
          const path = sourceMorphRefs.current[morph.id];
          if (!path) return;
          path.setAttribute('d', morph.interpolator(mapProgress));
          path.setAttribute('fill', interpolateColor(morph.fromColor, morph.toColor, mapProgress));
        });

        const linkReveal = clamp((mapProgress - 0.8) / 0.2, 0, 1);
        const linkFade = 1 - clamp((shatterProgress - 0.08) / 0.25, 0, 1);
        sankeyLayout.links.forEach((link) => {
          const path = flowLinkRefs.current[link.id];
          if (!path) return;
          path.style.opacity = (linkReveal * linkFade * 0.78).toFixed(3);
          path.style.strokeDashoffset = `${1 - linkReveal}`;
        });
      }
    });

    return () => {
      tween.scrollTrigger?.kill();
      tween.kill();
    };
  }, [prefersReducedMotion, sankeyLayout.links, sankeyLayout.sourceNodes, scrollyData.regions]);

  useLayoutEffect(() => {
    const shouldExpand = sankeyTreemapProgress >= 0.45;
    if (shouldExpand === shatterExpanded) return;

    if (prefersReducedMotion) {
      setShatterExpanded(shouldExpand);
      return;
    }

    const node = shatterNodeRef.current;
    if (!node) {
      setShatterExpanded(shouldExpand);
      return;
    }

    const state = Flip.getState(node);
    setShatterExpanded(shouldExpand);
    requestAnimationFrame(() => {
      Flip.from(state, {
        absolute: true,
        duration: 0.85,
        ease: 'power1.inOut',
        nested: true
      });
    });
  }, [prefersReducedMotion, sankeyTreemapProgress, shatterExpanded]);

  return (
    <main className={`infographic-shell ${prefersReducedMotion ? 'reduce-motion' : ''}`}>
      <p className="sr-only" aria-live="polite" aria-atomic="true">
        {liveNarrative}
      </p>

      <header className="infographic-topbar">
        <button className="infographic-nav-btn" onClick={onBack}>
          Back to dashboard
        </button>
        <p className="infographic-week">Week: {formatWeekRange(selectedWeek)}</p>
      </header>

      <section
        className="section-map-sankey-scroll"
        ref={(element) => {
          mapSankeySectionRef.current = element;
        }}
      >
        <div className="infographic-step section-map-sankey is-active">
          <div className="step-copy morph-copy">
            <div className={`copy-panel ${campaignMapProgress < 0.5 ? 'active' : ''}`}>
              <p className="step-kicker">Section 0 · The Poll Campaign</p>
              <h2>The survey was designed in three connected layers</h2>
              <p>
                Each response links healthcare ratings, perceived responsibility, and the news source
                shaping that opinion, so every later view tells one connected public story.
              </p>
              <br></br><br></br><br></br><br></br><br></br><br></br><br></br><br></br><br></br><br></br><br></br><br></br><br></br><br></br><br></br>
              <h2>Scroll down to explore the data</h2>
            </div>
            <div
              className={`copy-panel ${campaignMapProgress >= 0.5 && mapSankeyProgress < 0.5 ? 'active' : ''}`}
            >
              <p className="step-kicker">Section 1 · The National Pulse</p>
              <h2>How people rate healthcare across Cyprus</h2>
              <p>
                Compare regions at a glance and focus any district to see its health temperature,
                sample size, and local sentiment profile for the selected week.
              </p>
            </div>
            <div className={`copy-panel ${mapSankeyProgress >= 0.5 && sankeyTreemapProgress < 0.5 ? 'active' : ''}`}>
              <p className="step-kicker">Section 2 · The Fracture</p>
              <h2>Government support volume</h2>
              <p>
                This flow chart shows how government support is distributed across regions, revealing whether people are more likely to blame the current government for healthcare performance.
              </p>
            </div>
            <div className={`copy-panel ${sankeyTreemapProgress >= 0.5 && treemapTakeawayProgress < 0.5 ? 'active' : ''}`}>
              <p className="step-kicker">Section 3 · The Deep Dive</p>
              <h2>Where do mainstream media audiences put the blame?</h2>
              <p>
                This detailed breakdown shows how answer categories and media sources cluster together,
                revealing how different media outlets in Cyprus attribute pressure between government and broader causes.
              </p>
            </div>
            <div className={`copy-panel ${treemapTakeawayProgress >= 0.5 ? 'active' : ''}`}>
              <p className="step-kicker">Section 4 · Weekly Takeaways</p>
              <h2>What this week’s data suggests</h2>
              <p>
                Key signals summarized and aligned with trending news narratives to help interpret what’s driving public sentiment and where it might be headed.
              </p>
            </div>
          </div>

          <div className="sankey-stage map-sankey-stage is-live">
            <div
              className="map-sankey-layer"
              style={{
                opacity: mapSankeyLayerOpacity,
                pointerEvents: mapEntryOpacity > 0.55 ? 'auto' : 'none'
              }}
            >
              <svg viewBox="0 0 750 450" aria-label="Map to Sankey morph showing political blame flow">
                <defs>
                  <linearGradient id="sentiment-legend" x1="0%" y1="0%" x2="100%" y2="0%">
                    <stop offset="0%" stopColor="#d43f3a" />
                    <stop offset="50%" stopColor="#efcb46" />
                    <stop offset="100%" stopColor="#3dbb70" />
                  </linearGradient>
                </defs>
                <rect x="0" y="0" width="100%" height="100%" rx="24" className="sankey-bg" />
                <path
                  d={TEMPLATE_REGION_PATHS.cyjs1}
                  className="region-overlay secondary sankey-backdrop"
                  fill="#f2f3ef"
                  vectorEffect="non-scaling-stroke"
                  style={{ opacity: clamp(1 - mapSankeyProgress * 2, 0, 1) }}
                />
                <path
                  d={TEMPLATE_REGION_PATHS.cyjs2}
                  className="region-overlay secondary sankey-backdrop"
                  fill="#e0e2e2"
                  vectorEffect="non-scaling-stroke"
                  style={{ opacity: clamp(1 - mapSankeyProgress * 2, 0, 1) }}
                />

                {sankeyLayout.links.map((link) => {
                  const flowColor = link.target === 'antiGov' ? '#f05454' : '#3ddc84';
                  return (
                    <path
                      key={link.id}
                      ref={(element) => {
                        flowLinkRefs.current[link.id] = element;
                      }}
                      d={link.path}
                      stroke={flowColor}
                      strokeWidth={link.width}
                      strokeLinecap="round"
                      fill="none"
                      opacity={0}
                      pathLength={1}
                      strokeDasharray={1}
                      strokeDashoffset={1}
                    />
                  );
                })}

                {sankeyLayout.sourceNodes.map((sourceNode) => {
                  const region = scrollyData.regions.find((entry) => entry.id === sourceNode.id);
                  if (!region) return null;
                  const mapColor = pollScoreToColor(pollScoreFromSentiment(region.avgSentiment));
                  const labelY = sourceNode.y0 + (sourceNode.y1 - sourceNode.y0) / 2 + 4;
                  const highlighted = hoveredRegion === region.id;

                  return (
                    <g key={sourceNode.id}>
                      <path
                        ref={(element) => {
                          sourceMorphRefs.current[sourceNode.id] = element;
                        }}
                        d={TEMPLATE_REGION_PATHS[SVG_REGION_ID_MAP[sourceNode.id as CyprusLocation]]}
                        className={`region-overlay primary sankey-morph-shape ${highlighted ? 'active' : ''}`}
                        vectorEffect="non-scaling-stroke"
                        fill={mapColor}
                        tabIndex={0}
                        role="button"
                        aria-pressed={selectedRegion === sourceNode.id}
                        aria-label={`${sourceNode.name}. Health temperature ${region.healthTemperature} out of 100. Sample ${formatNumber(region.sample)}.`}
                        onPointerEnter={() => setHoveredRegion(region.id)}
                        onPointerLeave={() => setHoveredRegion(null)}
                        onFocus={() => setHoveredRegion(region.id)}
                        onBlur={() => setHoveredRegion(null)}
                        onClick={() => setSelectedRegion(region.id)}
                        onKeyDown={(event) => {
                          if (event.key === 'Enter' || event.key === ' ') {
                            event.preventDefault();
                            setSelectedRegion(region.id);
                          }
                        }}
                      />
                      <text
                        x={SVG_REGION_LABELS[region.id].x}
                        y={SVG_REGION_LABELS[region.id].y}
                        className="region-label"
                        style={{ opacity: mapLabelOpacity }}
                      >
                        {region.name}
                      </text>
                      <text
                        x={sourceNode.x1 + 10}
                        y={labelY}
                        className="node-label"
                        style={{ opacity: sankeyLabelOpacity }}
                      >
                        {sourceNode.name}
                      </text>
                    </g>
                  );
                })}

                {sankeyLayout.targetNodes.map((targetNode) => {
                  const targetOpacity = clamp((mapSankeyProgress - 0.45) / 0.25, 0, 1);
                  const scaleLabel = targetNode.id === 'antiGov' ? 'Critical' : 'Positive';
                  const targetFill = targetNode.id === 'antiGov' ? '#f05454' : '#3ddc84';
                  return (
                    <g key={targetNode.id}>
                      <path
                        d={rectPath(targetNode.x0, targetNode.y0, targetNode.x1, targetNode.y1)}
                        className={`sankey-node ${targetNode.id === 'antiGov' ? 'anti' : 'pro'}`}
                        opacity={targetOpacity}
                        fill={targetFill}
                      />
                      <text
                        x={targetNode.x0 - 12}
                        y={targetNode.y0 + (targetNode.y1 - targetNode.y0) / 2 + 4}
                        className="node-label strong"
                        textAnchor="end"
                        style={{ opacity: sankeyLabelOpacity }}
                      >
                        {scaleLabel}
                      </text>
                    </g>
                  );
                })}

                <rect
                  x="64"
                  y="404"
                  width="240"
                  height="14"
                  rx="7"
                  fill="url(#sentiment-legend)"
                  style={{ opacity: mapUiOpacity }}
                />
                <text x="64" y="398" className="legend-text" style={{ opacity: mapUiOpacity }}>
                  Critical
                </text>
                <text
                  x="304"
                  y="398"
                  className="legend-text"
                  textAnchor="end"
                  style={{ opacity: mapUiOpacity }}
                >
                  Positive
                </text>
              </svg>

              {activeRegion && (
                <aside
                  className={`map-insight-card ${hoveredRegion ? 'is-hovered' : ''}`}
                  aria-label="Selected region metrics"
                  style={{
                    opacity: mapUiOpacity,
                    visibility: mapUiOpacity < 0.05 ? 'hidden' : 'visible'
                  }}
                >
                  <p>{activeRegion.name}</p>
                  <span>Health Temperature: {activeRegion.healthTemperature} / 100</span>
                  <span>Sample respondents: {formatNumber(activeRegion.sample)}</span>
                  <span>Blame current government: {Math.round(activeRegion.antiWeight * 100)}%</span>
                </aside>
              )}
            </div>

            <div
              className="poll-campaign-layer"
              style={{
                opacity: campaignLayerOpacity,
                visibility: campaignLayerOpacity < 0.04 ? 'hidden' : 'visible',
                pointerEvents: campaignLayerOpacity > 0.2 ? 'auto' : 'none'
              }}
            >
              <div className={`poll-campaign-card ${campaignLayerOpacity < 0.92 ? 'is-exiting' : ''}`}>
                <h3>Programmatic Poll Layers</h3>
                <p>
                  Each respondent answered a sequence that links service experience, responsibility, and media influence.
                </p>
                <div className="poll-layer-list">
                  {POLL_CAMPAIGN_LAYERS.map((layer) => (
                    <article key={layer.id} className="poll-layer-item">
                      <p className="poll-layer-title">{layer.title}</p>
                      <h4>{layer.question}</h4>
                      <div className="poll-layer-options">
                        {layer.options.map((option) => (
                          <span key={`${layer.id}-${option}`}>{option}</span>
                        ))}
                      </div>
                    </article>
                  ))}
                </div>
              </div>
            </div>

            <div
              ref={shatterStageRef}
              className={`treemap-morph-layer ${shatterExpanded ? 'is-expanded' : 'is-collapsed'}`}
              style={{
                opacity: treemapVisibleOpacity,
                visibility: treemapVisibleOpacity < 0.04 ? 'hidden' : 'visible',
                pointerEvents: treemapVisibleOpacity > 0.72 ? 'auto' : 'none'
              }}
            >
              <div
                ref={shatterNodeRef}
                className="shatter-node responsibility-node"
                style={shatterExpanded ? undefined : shatterSlotStyle}
              >
                <h3>Healthcare Status Responsibility</h3>
                <div className="shatter-treemap-wrap">
                  <svg
                    className="shatter-treemap-svg"
                    viewBox={`0 0 ${TREEMAP_CANVAS_WIDTH} ${TREEMAP_CANVAS_HEIGHT}`}
                    preserveAspectRatio="xMidYMid meet"
                    aria-label="Nested treemap for healthcare responsibility by source and answer"
                  >
                    {sectionTreemapLayout.sources.map((sourceNode) => {
                      const width = sourceNode.x1 - sourceNode.x0;
                      const height = sourceNode.y1 - sourceNode.y0;
                      const canShowLabel = width > 52 && height > 20;
                      const sourceScale = clamp(Math.min(width, height) / 210, 0.62, 1.35);
                      const sourceFontSize = Math.round(11 + sourceScale * 8);
                      const sourceLineHeight = Math.round(sourceFontSize * 1.08);
                      const sourceMaxLines = height > 70 ? 2 : 1;
                      const sourceMaxChars = Math.max(
                        4,
                        Math.floor((width - 14) / Math.max(sourceFontSize * 0.58, 1))
                      );
                      const sourceLines = wrapLabelLines(
                        sourceNode.data.name,
                        sourceMaxChars,
                        sourceMaxLines
                      );
                      const sourceTextTop = sourceNode.y0 + 8 + sourceFontSize;
                      return (
                        <g key={`source-${sourceNode.data.name}`}>
                          <rect
                            x={sourceNode.x0}
                            y={sourceNode.y0}
                            width={width}
                            height={height}
                            className="treemap-region-rect"
                          />
                          {canShowLabel && (
                            <text
                              x={sourceNode.x0 + 8}
                              y={sourceTextTop}
                              className="treemap-region-label"
                              style={{ fontSize: `${sourceFontSize}px` }}
                            >
                              {sourceLines.map((line, index) => (
                                <tspan
                                  key={`${sourceNode.data.name}-${index}`}
                                  x={sourceNode.x0 + 8}
                                  y={sourceTextTop + index * sourceLineHeight}
                                >
                                  {line}
                                </tspan>
                              ))}
                            </text>
                          )}
                        </g>
                      );
                    })}

                    {sectionTreemapLayout.answers.map((answerNode) => {
                      const width = answerNode.x1 - answerNode.x0;
                      const height = answerNode.y1 - answerNode.y0;
                      const sentiment = answerNode.data.sentiment ?? 0;
                      const canShowLabel = width > 44 && height > 20;
                      const canShowValue = width > 52 && height > 22;
                      const answerScale = clamp(Math.min(width, height) / 150, 0.72, 1.8);
                      const answerFontSize = Math.round(10 + answerScale * 8);
                      const answerLineHeight = Math.round(answerFontSize * 1.02);
                      const valueFontSize = Math.round(13 + answerScale * 8);
                      const answerMaxLabelHeight = Math.max(
                        10,
                        height - (canShowValue ? valueFontSize + 12 : 8) - 8
                      );
                      const answerMaxLines = Math.max(
                        1,
                        Math.min(3, Math.floor(answerMaxLabelHeight / Math.max(answerLineHeight, 1)))
                      );
                      const answerMaxChars = Math.max(
                        4,
                        Math.floor((width - 12) / Math.max(answerFontSize * 0.56, 1))
                      );
                      const answerLines = wrapLabelLines(
                        answerNode.data.name,
                        answerMaxChars,
                        answerMaxLines
                      );
                      const answerTextTop = answerNode.y0 + 6 + answerFontSize;
                      return (
                        <g key={`answer-${answerNode.data.name}-${answerNode.parent?.data.name ?? 'root'}`}>
                          <rect
                            x={answerNode.x0}
                            y={answerNode.y0}
                            width={width}
                            height={height}
                            className="treemap-answer-rect"
                            fill={scoreToColor(sentiment, weeklyProfile.saturation)}
                            opacity={0.9}
                          />
                          {canShowLabel && (
                            <text
                              x={answerNode.x0 + 8}
                              y={answerTextTop}
                              className="treemap-answer-label"
                              style={{ fontSize: `${answerFontSize}px` }}
                            >
                              {answerLines.map((line, index) => (
                                <tspan
                                  key={`${answerNode.data.name}-${index}`}
                                  x={answerNode.x0 + 8}
                                  y={answerTextTop + index * answerLineHeight}
                                >
                                  {line}
                                </tspan>
                              ))}
                            </text>
                          )}
                          {canShowValue && (
                            <text
                              x={answerNode.x0 + 6}
                              y={answerNode.y1 - 6}
                              className="treemap-leaf-value"
                              style={{ fontSize: `${valueFontSize}px` }}
                            >
                              {formatNumber(answerNode.data.value ?? 0)}
                            </text>
                          )}
                        </g>
                      );
                    })}
                  </svg>
                  <div className="treemap-source-legend" aria-hidden="true">
                    <div className="treemap-source-legend-bar" />
                    <div className="treemap-source-legend-labels">
                      <span>Critical</span>
                      <span>Positive</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div
              className="takeaways-morph-layer"
              style={{
                opacity: takeawayLayerOpacity,
                visibility: takeawayLayerOpacity < 0.04 ? 'hidden' : 'visible',
                pointerEvents: takeawayLayerOpacity > 0.65 ? 'auto' : 'none'
              }}
            >
              <div className={`takeaway-table-card ${takeawayLayerOpacity > 0.5 ? 'is-visible' : ''}`}>
                <h3>Key Takeaways Linked To Weekly Signals</h3>
                <p>
                  Snapshot for <strong>{formatWeekRange(selectedWeek)}</strong>. 
                </p>
                <div className="takeaway-table-wrap">
                  <table className="takeaway-table" aria-label="Weekly key takeaways and possible news drivers">
                    <thead>
                      <tr>
                        <th scope="col">Signal</th>
                        <th scope="col">What the data shows</th>
                        <th scope="col">Possible related news</th>
                        <th scope="col">Why it matters</th>
                      </tr>
                    </thead>
                    <tbody>
                      {takeawayRows.map((row) => (
                        <tr key={row.signal}>
                          <th scope="row">{row.signal}</th>
                          <td>{row.whatChanged}</td>
                          <td>{row.possibleDriver}</td>
                          <td>{row.whyItMatters}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <div className="timeline-sticky">
        <label htmlFor="week-slider">Poll evolution</label>
        <input
          id="week-slider"
          type="range"
          min={0}
          max={INFOGRAPHIC_WEEKS.length - 1}
          step={1}
          value={weekIndex}
          onChange={(event) => setWeekIndex(Number(event.target.value))}
        />
        <span>{formatWeekRange(selectedWeek)}</span>
      </div>
    </main>
  );
}

export default InfographicPage;
